import { useNotifications } from '@isbj-redesign/shared-components'
import { useMemo } from 'react'
import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from 'react-query'
import wretch, { WretcherError, WretcherErrorCallback, WretcherResponse } from 'wretch'
import { useLoginRedirect } from './auth'
import { useEncryption } from './EncryptionProvider'
import { BereitstellungListItem, Dokument, ErrorLike, Seite } from './types'

const backend = wretch().url('/api')

export type QuerySeitenProps = { suche?: string; sort?: string; start?: number; anzahl?: number }

export function useQueryDokumente(props: QuerySeitenProps, options?: UseQueryOptions<Seite<Dokument>, ErrorLike, Seite<Dokument>>) {
    const { addErrorNotification } = useNotifications()
    const redirectToLogin = useLoginRedirect()
    const filteredProps = useMemo(() => filterObject(props, (it) => it !== undefined), [props])
    return useQuery<Seite<Dokument>, ErrorLike>(
        ['dokumente', filteredProps],
        () =>
            backend
                .url('/dokumente')
                .query(filteredProps)
                .headers({ 'cache-control': 'no-cache' })
                .get()
                .error(403, redirectToLogin)
                .fetchError(errorHandler('Bei der Abfrage der Dokumenten ist ein Fehler aufgetreten.'))
                .json(),
        {
            onError: (error) => addErrorNotification(error.message),
            ...options,
        }
    )
}

export function useQueryBereitstellungen(
    props: QuerySeitenProps,
    options?: UseQueryOptions<Seite<BereitstellungListItem>, ErrorLike, Seite<BereitstellungListItem>>
) {
    const { addErrorNotification } = useNotifications()
    const redirectToLogin = useLoginRedirect()
    const filteredProps = useMemo(() => filterObject(props, (it) => it !== undefined), [props])
    return useQuery<Seite<BereitstellungListItem>, ErrorLike>(
        ['bereitstellungen', filteredProps],
        () =>
            backend
                .url('/bereitstellungen')
                .query(filteredProps)
                .headers({ 'cache-control': 'no-cache' })
                .get()
                .error(403, redirectToLogin)
                .fetchError(errorHandler('Bei der Abfrage der Bereitstellungen ist ein Fehler aufgetreten.'))
                .json(),
        {
            onError: (error) => addErrorNotification(error.message),
            ...options,
        }
    )
}

export function useQueryBereitstellung(bereitstellungId: string, options?: UseQueryOptions<BereitstellungListItem, ErrorLike, BereitstellungListItem>) {
    const redirectToLogin = useLoginRedirect()
    return useQuery<BereitstellungListItem, ErrorLike>(
        ['bereitstellung', bereitstellungId],
        () =>
            backend
                .url(`/bereitstellungen/${encodeURI(bereitstellungId)}`)
                .headers({ 'cache-control': 'no-cache' })
                .get()
                .error(403, redirectToLogin)
                .fetchError(errorHandler('Bei der Abfrage der Bereitstellung ist ein Fehler aufgetreten.'))
                .notFound(() => undefined)
                .json(),
        options
    )
}

export function useMutateBereitstellungDaten(bereitstellungId: string, options?: UseMutationOptions<Blob, ErrorLike>) {
    const redirectToLogin = useLoginRedirect()
    return useMutation<Blob, ErrorLike>(
        ['bereitstellung', bereitstellungId],
        () =>
            backend
                .url(`/bereitstellungen/${encodeURI(bereitstellungId)}/daten`)
                .headers({ 'cache-control': 'no-cache' })
                .get()
                .error(403, redirectToLogin)
                .fetchError(errorHandler('Bei der Abfrage der Bereitstellung ist ein Fehler aufgetreten.'))
                .notFound(() => undefined)
                .blob(),
        options
    )
}

export type MutationBereistellenProps = { dokumente: Dokument[]; dateiname?: string }

export function useMutationBereitstellen(options?: UseMutationOptions<WretcherResponse, ErrorLike, MutationBereistellenProps>) {
    const { oeffentlicherNutzerSchluesselJwk, schluesselUuid } = useEncryption()
    const queryClient = useQueryClient()
    const { addErrorNotification } = useNotifications()
    const redirectToLogin = useLoginRedirect()

    return useMutation<WretcherResponse, ErrorLike, MutationBereistellenProps>(
        ({ dokumente, dateiname }) =>
            backend
                .url('/bereitstellungen')
                .json({ dokumentIds: dokumente.map((d) => d.id), oeffentlicherNutzerSchluesselJwk, schluesselUuid, dateiname })
                .post()
                .error(403, redirectToLogin)
                .badRequest(errorHandler('Bei der Anfrage ist ein Fehler aufgetreten.'))
                .fetchError(errorHandler('Bei der Anfrage ist ein Fehler aufgetreten.'))
                .res(),
        {
            onError: (error) => addErrorNotification(error.message),
            onSettled: () => {
                queryClient.invalidateQueries('dokumente')
                queryClient.invalidateQueries('bereitstellungen')
            },
            ...options,
        }
    )
}

function errorHandler(defaultMessage: string): WretcherErrorCallback {
    return async (error: WretcherError) => {
        if (process.env.NODE_ENV !== 'production') console.error('Fetch error:', error)
        throw createError(error, defaultMessage)
    }
}

function createError(error: WretcherError, defaultMessage: string): ErrorLike {
    const message = getErrorMessage(error, defaultMessage)
    return new Error(message)
}

function getErrorMessage(error: WretcherError, defaultMessage: string): string {
    if (error.text) {
        try {
            const data = JSON.parse(error.text)
            if (data.message) return data.message
        } catch (e) {
            return error.text
        }
    }

    return error.message || defaultMessage
}

function filterObject<Value>(object: Record<string, Value>, predicate: (value: Value) => boolean) {
    return Object.keys(object)
        .filter((key) => predicate(object[key]))
        .reduce((acc, key) => ({ ...acc, [key]: object[key] }), {})
}
